/**
 * What: Opens Shopify's native product picker, the Study Builder's only product chooser.
 * Transport / data-flow: Shopify owns catalog search and selection. The chosen GID is forwarded verbatim
 * as one selectedVariant edit; the Worker reads and stores the selected product. Cancel changes nothing.
 * Picker failures use the existing page lifecycle feedback, never a second catalog or modal.
 */
import { useCallback } from 'react'
import type { shopifyApiFetch } from '../../shopifyApiFetch'
import { isProductSelectionLocked } from './studyBuilderDrafts'
import type { StudyBuilderLifecycle } from './studyBuilderLifecycle'

type ShopifyPickerBridge = Parameters<typeof shopifyApiFetch>[0] & {
  resourcePicker?: (options: {
    type: 'product'
    action: 'select'
    multiple: false
    filter: { variants: false }
  }) => Promise<Array<{ id?: string }> | undefined>
}

export function useStudyBuilderProductPicker(input: {
  shopify: Parameters<typeof shopifyApiFetch>[0]
  form: { state: { values: { productSelectionLocked?: boolean } } }
  lifecycle: StudyBuilderLifecycle
  onSelectVariant: (value: string) => void
}) {
  const { shopify, form, lifecycle, onSelectVariant } = input
  const handleOpenProductPicker = useCallback(async () => {
    if (isProductSelectionLocked(form.state.values)) return
    await lifecycle.run('choose-product', async (signal) => {
      const resourcePicker = (shopify as ShopifyPickerBridge).resourcePicker
      if (typeof resourcePicker !== 'function')
        throw new Error('SHOPIFY_PRODUCT_PICKER_UNAVAILABLE: Shopify product picker is unavailable.')
      // No selectionIds: pre-ticking the current product makes single-select replacement require an extra untick.
      const products = await resourcePicker({
        type: 'product',
        action: 'select',
        multiple: false,
        filter: { variants: false },
      })
      signal.throwIfAborted()
      const picked = products?.[0]
      if (!picked) throw new DOMException('SHOPIFY_PRODUCT_PICKER_CANCELLED', 'AbortError')
      if (!picked.id?.trim())
        throw new Error('SHOPIFY_PRODUCT_PICKER_INVALID_SELECTION: Shopify returned a product without an ID.')
      onSelectVariant(picked.id)
    })
  }, [form, lifecycle, onSelectVariant, shopify])
  return { handleOpenProductPicker }
}
